import { useCallback, useEffect, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from "lexical";
import { $isImageNode } from "../ImageNode";
import { ImageResizer } from "./ImageResizer";

interface ImageComponentProps {
  src: string;
  altText: string;
  width?: number;
  nodeKey: string;
  resizable: boolean;
}

export function ImageComponent({
  src,
  altText,
  width,
  nodeKey,
  resizable,
}: ImageComponentProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSelected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [maxWidth, setMaxWidth] = useState(800);

  // Calculate max width based on container and update on resize
  useEffect(() => {
    const updateMaxWidth = () => {
      const container = containerRef.current?.closest(".editor-input");
      if (container) {
        const rect = container.getBoundingClientRect();
        // Leave some padding
        setMaxWidth(rect.width - 32);
      }
    };

    updateMaxWidth();
    window.addEventListener("resize", updateMaxWidth);
    return () => window.removeEventListener("resize", updateMaxWidth);
  }, []);

  const onDelete = useCallback(
    (event: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        event.preventDefault();
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if ($isImageNode(node)) {
            node.remove();
          }
        });
        return true;
      }
      return false;
    },
    [editor, isSelected, nodeKey]
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (
        event.target === imageRef.current ||
        containerRef.current?.contains(event.target as Node)
      ) {
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }
      return false;
    },
    [isSelected, setSelected, clearSelection]
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        onClick,
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        onDelete,
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        onDelete,
        COMMAND_PRIORITY_LOW
      )
    );

    return () => {
      unregister();
    };
  }, [editor, onClick, onDelete]);

  const onResizeEnd = useCallback(
    (newWidth: number) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) {
          node.setWidth(newWidth);
        }
      });
    },
    [editor, nodeKey]
  );

  return (
    <div
      ref={containerRef}
      className={`image-wrapper ${isSelected ? "focused" : ""}`}
      draggable={false}
    >
      <img
        ref={imageRef}
        src={src}
        alt={altText}
        style={{
          width: width ? `${width}px` : "auto",
          maxWidth: "100%",
          display: "block",
        }}
        draggable={false}
      />
      {resizable && isSelected && (
        <ImageResizer
          imageRef={imageRef}
          onResizeEnd={onResizeEnd}
          maxWidth={maxWidth}
        />
      )}
    </div>
  );
}
